import { Cta, SanityCtaProps } from "@/components/cta"
import { SocialIconLink } from "@/components/social-icon-link"
import { invokeFunnelCompany } from "@/features/funnel/api/company"
import { useLocaleContext } from "@/modules/i18n/context"
import { RichText, RichTextValue } from "@/components/rich-text"
import { Checkbox } from "@/ui/checkbox"
import { Input } from "@/ui/input"
import { Textarea } from "@/ui/textarea"
import { zodResolver } from "@hookform/resolvers/zod"
import useEmblaCarousel from "embla-carousel-react"
import { ArrowRight, CircleArrowLeft, LoaderCircle } from "lucide-react"
import { ReactNode, useEffect, useMemo, useRef, useState } from "react"
import { Controller, useForm } from "react-hook-form"
import { SanityImageAsset, SanityReference } from "sanity-codegen"
import { toast } from "sonner"
import z from "zod"
import { removeTrafficSource, retrieveTrafficSource, useTrafficSource } from "@/modules/tracking/TrafficSourceProvider"

const ANSWER_VALUE = {
  ONE_FREELANCE: "1",
  TWO_FREELANCES: "2",
  THREE_PLUS_FREELANCES: "3+",
  NOW: "Now",
  TWO_WEEKS: "2w",
  FOUR_WEEKS: "4w",
  MORE_THAN_ONE_MONTH: "1m+",
  UNKNOWN: "Unknown",
  CONTACT_BY_YOU: "contact_by_you",
  WORD_OF_MOUTH: "word_of_mouth",
  LINKEDIN: "linkedin",
  INSTAGRAM: "instagram",
  TIKTOK: "tiktok",
  NEWSPAPERS_AND_PRESS: "newspapers_and_press",
  OTHER: "other",
} as const

type AnswerValue = (typeof ANSWER_VALUE)[keyof typeof ANSWER_VALUE]

interface FunnelCompanyProps {
  content: {
    sisterFunnelIntro?: RichTextValue
    sisterFunnelCta?: SanityCtaProps
    socials: {
      title?: string
      url: string
      image?: {
        asset: SanityReference<SanityImageAsset>
      }
    }[]
    steps: {
      alertTitle?: string
      alertDescription?: string
      type?: "singleChoice" | "freeText"
      question: string
      answerPlaceholder?: string
      answers: {
        title: string
        subtitle?: string
        value?: AnswerValue
        allowFreeText?: boolean
        freeTextPlaceholder?: string
      }[]
    }[]
    contactForm: {
      title: string
      nameLabel: string
      surnameLabel?: string
      roleLabel?: string
      companyLabel: string
      emailLabel: string
      phoneNumberLabel: string
      activityLabel: string
      privacyLink: SanityCtaProps
      ctaLabel: string
      successTitle: string
      successMessage: string
    }
  }
}

const createCompanyDetailsSchema = (messages: {
  nameRequired: string
  surnameRequired: string
  roleRequired: string
  companyRequired: string
  emailInvalid: string
  phoneInvalid: string
  activityRequired: string
}) =>
  z.object({
    name: z.string().trim().min(1, messages.nameRequired),
    surname: z.string().trim().min(1, messages.surnameRequired),
    role: z.string().trim().min(1, messages.roleRequired),
    company: z.string().trim().min(1, messages.companyRequired),
    email: z.string().trim().email(messages.emailInvalid),
    phoneNumber: z
      .string()
      .trim()
      .refine((value) => !value || /^\+?[0-9().\-\s]{7,20}$/.test(value), {
        message: messages.phoneInvalid,
      }),
    activity: z.string().trim().min(1, messages.activityRequired).max(200),
  })

type CompanyDetailsData = z.infer<ReturnType<typeof createCompanyDetailsSchema>>
type FunnelCompanyStep = FunnelCompanyProps["content"]["steps"][number]
type FunnelCompanyAnswer = FunnelCompanyStep["answers"][number]
type StepAnswerState = {
  question: string
  answer: string
  selectedOptionTitle: string,
  value: AnswerValue,
  freeTextValue: string
}

const formatSingleChoiceAnswer = (title: string, freeText: string) => {
  const trimmedFreeText = freeText.trim()
  if (!trimmedFreeText) {
    return ""
  }

  return `${title}: ${trimmedFreeText}`
}

export function FunnelCompany({ content }: FunnelCompanyProps) {
  const { locale, $: translations } = useLocaleContext()
  const $ = translations.funnelCompany
  const [emblaRef, api] = useEmblaCarousel({
    axis: "x",
    watchDrag: false,
  })

  const [activeStep, setActiveStep] = useState(0)
  const [companyDetails, setCompanyDetails] = useState<CompanyDetailsData>({
    name: "",
    surname: "",
    role: "",
    company: "",
    email: "",
    phoneNumber: "",
    activity: "",
  })
  const [stepAnswers, setStepAnswers] = useState<StepAnswerState[]>(
    content.steps.map((step) => ({
      question: step.question,
      answer: "",
      value: ANSWER_VALUE.UNKNOWN,
      selectedOptionTitle: "",
      freeTextValue: "",
    }))
  )
  const [privacyAccepted, setPrivacyAccepted] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)

  useEffect(() => {
    if (!api) return
    const onSelect = () => setActiveStep(api.selectedScrollSnap())
    api.on("select", onSelect)
    onSelect()
    return () => {
      api.off("select", onSelect)
    }
  }, [api])

  useEffect(() => {
    const url = new URL(window.location.href)
    url.searchParams.set("step", String(activeStep + 1))
    window.history.replaceState({}, "", url)
  }, [activeStep])

  const updateFreeTextStepAnswer = (stepIndex: number, answer: string) => {
    setStepAnswers((prev) => {
      const updated = [...prev]
      updated[stepIndex] = {
        ...updated[stepIndex],
        answer,
        selectedOptionTitle: "",
        freeTextValue: "",
      }
      return updated
    })
  }

  const selectSingleChoiceAnswer = (
    stepIndex: number,
    selectedAnswer: FunnelCompanyAnswer
  ) => {
    setStepAnswers((prev) => {
      const updated = [...prev]
      const currentStepAnswer = updated[stepIndex]
      const isSameAnswerSelected =
        currentStepAnswer.selectedOptionTitle === selectedAnswer.title
      const freeTextValue =
        selectedAnswer.allowFreeText && isSameAnswerSelected
          ? currentStepAnswer.freeTextValue
          : ""

      updated[stepIndex] = {
        ...currentStepAnswer,
        selectedOptionTitle: selectedAnswer.title,
        value: selectedAnswer.value!,
        freeTextValue,
        answer: selectedAnswer.allowFreeText
          ? formatSingleChoiceAnswer(selectedAnswer.title, freeTextValue)
          : selectedAnswer.title,
      }
      return updated
    })
  }

  const updateSingleChoiceFreeText = (stepIndex: number, freeText: string) => {
    setStepAnswers((prev) => {
      const updated = [...prev]
      const currentStepAnswer = updated[stepIndex]

      updated[stepIndex] = {
        ...currentStepAnswer,
        freeTextValue: freeText,
        answer: currentStepAnswer.selectedOptionTitle
          ? formatSingleChoiceAnswer(
              currentStepAnswer.selectedOptionTitle,
              freeText
            )
          : "",
      }
      return updated
    })
  }

  const lastStepIndex = content.steps.length - 1
  const canSubmitFinalStep =
    lastStepIndex >= 0 &&
    !!stepAnswers[lastStepIndex]?.answer.trim() &&
    privacyAccepted

  const trafficSource = useTrafficSource()

  return (
    <div className="overflow-hidden py-8" ref={emblaRef}>
      <div className="flex w-full">
        <CompanyDetailsStep
          contactForm={content.contactForm}
          sisterFunnelIntro={content.sisterFunnelIntro}
          sisterFunnelCta={content.sisterFunnelCta}
          totalSteps={content.steps.length + 1}
          active={activeStep === 0}
          onContinue={(data) => {
            setCompanyDetails(data)
            api?.scrollNext()
          }}
        />

        {content.steps.map((step, i) => (
          <StepSlide
            key={i}
            step={step}
            stepNumber={i + 2}
            totalSteps={content.steps.length + 1}
            selectedAnswer={stepAnswers[i]?.answer || ""}
            selectedOptionTitle={stepAnswers[i]?.selectedOptionTitle || ""}
            selectedFreeTextValue={stepAnswers[i]?.freeTextValue || ""}
            ctaLabel={content.contactForm.ctaLabel}
            privacyLink={
              <div className="text-inherit">
                {$.privacy}
                <Cta
                  className="w-fit text-primary-500"
                  variant="blank"
                  sanity={content.contactForm.privacyLink}
                />
              </div>
            }
            isLastStep={i === lastStepIndex}
            isSubmitting={isSubmitting}
            canSubmit={canSubmitFinalStep && !isSubmitting}
            active={activeStep === i + 1}
            privacyAccepted={privacyAccepted}
            onPrivacyChange={setPrivacyAccepted}
            onBack={() => api?.scrollPrev()}
            onFreeTextStepAnswerChange={(answer) =>
              updateFreeTextStepAnswer(i, answer)
            }
            onAnswerSelect={(answer) => selectSingleChoiceAnswer(i, answer)}
            onSelectedAnswerFreeTextChange={(freeText) =>
              updateSingleChoiceFreeText(i, freeText)
            }
            onContinue={() => api?.scrollNext()}
            onSubmit={async () => {
              setIsSubmitting(true)
              const result = await invokeFunnelCompany({
                type: "company",
                locale,
                ...companyDetails,
                steps: stepAnswers.map(({ question, answer, value }) => ({
                  question,
                  answer,
                  value,
                })),
                trafficSource: trafficSource,
              })
              setIsSubmitting(false)

              if (result) {
                removeTrafficSource()
                api?.scrollNext()
              } else {
                toast($.errorMessage)
              }
            }}
          />
        ))}

        <Success
          title={content.contactForm.successTitle}
          description={content.contactForm.successMessage}
          active={activeStep === content.steps.length + 1}
          socials={content.socials}
        />
      </div>
    </div>
  )
}

function CompanyDetailsStep({
  contactForm,
  sisterFunnelIntro,
  sisterFunnelCta,
  totalSteps,
  active,
  onContinue,
}: {
  contactForm: FunnelCompanyProps["content"]["contactForm"]
  sisterFunnelIntro?: RichTextValue
  sisterFunnelCta?: SanityCtaProps
  totalSteps: number
  active: boolean
  onContinue: (data: CompanyDetailsData) => void
}) {
  const $ = useLocaleContext().$.funnelCompany
  const companyDetailsSchema = useMemo(
    () => createCompanyDetailsSchema($.validation),
    [$.validation]
  )
  const {
    control,
    watch,
    handleSubmit,
    formState: { errors },
  } = useForm<CompanyDetailsData>({
    defaultValues: {
      name: "",
      surname: "",
      role: "",
      company: "",
      email: "",
      phoneNumber: "",
      activity: "",
    },
    mode: "onSubmit",
    reValidateMode: "onChange",
    resolver: zodResolver(companyDetailsSchema),
  })

  const values = watch()
  const activity = values.activity || ""
  const canProceed = companyDetailsSchema.safeParse(values).success

  return (
    <EmblaSlide active={active}>
      <div className="mx-auto flex max-w-[138rem] justify-center">
        <form
          onSubmit={handleSubmit((data) => onContinue(data))}
          className="w-full md:w-1/2"
        >
          <p className="font-family-gtpressura text-xs font-light text-neutral-dark">
            Step 1 {$.of} {totalSteps}
          </p>
          <h2 className="mt-6 font-family-gthaptik text-xl font-light text-primary-800">
            {contactForm.title}
          </h2>
          <div className="mt-4 flex flex-col gap-4 md:flex-row">
            <div className="flex-1">
              <Controller
                name="name"
                control={control}
                render={({ field }) => (
                  <Input
                    label={contactForm.nameLabel}
                    className="w-full"
                    required
                    value={field.value}
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                    name={field.name}
                  />
                )}
              />
              {errors.name?.message && (
                <p className="text-red-700 mt-2 font-family-gtpressura text-sm">
                  {errors.name.message}
                </p>
              )}
            </div>
            <div className="flex-1">
              <Controller
                name="surname"
                control={control}
                render={({ field }) => (
                  <Input
                    label={contactForm.surnameLabel || "Cognome"}
                    className="w-full"
                    required
                    value={field.value}
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                    name={field.name}
                  />
                )}
              />
              {errors.surname?.message && (
                <p className="text-red-700 mt-2 font-family-gtpressura text-sm">
                  {errors.surname.message}
                </p>
              )}
            </div>
          </div>
          <div className="mt-4 flex flex-col gap-4 md:flex-row">
            <div className="flex-1">
              <Controller
                name="role"
                control={control}
                render={({ field }) => (
                  <Input
                    label={contactForm.roleLabel || "Ruolo"}
                    className="w-full"
                    required
                    value={field.value}
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                    name={field.name}
                  />
                )}
              />
              {errors.role?.message && (
                <p className="text-red-700 mt-2 font-family-gtpressura text-sm">
                  {errors.role.message}
                </p>
              )}
            </div>
            <div className="flex-1">
              <Controller
                name="company"
                control={control}
                render={({ field }) => (
                  <Input
                    label={contactForm.companyLabel}
                    className="w-full"
                    required
                    value={field.value}
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                    name={field.name}
                  />
                )}
              />
              {errors.company?.message && (
                <p className="text-red-700 mt-2 font-family-gtpressura text-sm">
                  {errors.company.message}
                </p>
              )}
            </div>
          </div>
          <div className="mt-4 flex flex-col gap-4 md:flex-row">
            <div className="flex-1">
              <Controller
                name="email"
                control={control}
                render={({ field }) => (
                  <Input
                    label={contactForm.emailLabel}
                    className="w-full"
                    type="email"
                    required
                    value={field.value}
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                    name={field.name}
                  />
                )}
              />
              {errors.email?.message && (
                <p className="text-red-700 mt-2 font-family-gtpressura text-sm">
                  {errors.email.message}
                </p>
              )}
            </div>
            <div className="flex-1">
              <Controller
                name="phoneNumber"
                control={control}
                render={({ field }) => (
                  <Input
                    label={contactForm.phoneNumberLabel}
                    className="w-full"
                    type="tel"
                    value={field.value}
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                    name={field.name}
                  />
                )}
              />
              {errors.phoneNumber?.message && (
                <p className="text-red-700 mt-2 font-family-gtpressura text-sm">
                  {errors.phoneNumber.message}
                </p>
              )}
            </div>
          </div>
          <Controller
            name="activity"
            control={control}
            render={({ field }) => (
              <Textarea
                label={
                  contactForm.activityLabel ||
                  "Per quale attività possiamo esserti utili?"
                }
                className="mt-4"
                required
                value={field.value}
                onChange={field.onChange}
                onBlur={field.onBlur}
                name={field.name}
              />
            )}
          />
          {errors.activity?.message && (
            <p className="text-red-700 mt-2 font-family-gtpressura text-sm">
              {errors.activity.message}
            </p>
          )}
          <p className="font-family-gtpressura text-sm font-light text-neutral-dark">
            {activity.length} / 200
          </p>
          <Cta
            className="mt-8 flex items-center gap-2"
            type="submit"
            disabled={!active || !canProceed}
          >
            {$.proceed}
          </Cta>
          <div className="mt-24">
            {sisterFunnelIntro?.length ? (
              <RichText className="text-left" value={sisterFunnelIntro} />
            ) : null}
            {sisterFunnelCta ? (
              <div className="mt-8 flex justify-center">
                <Cta className="md:mr-auto" sanity={sisterFunnelCta} />
              </div>
            ) : null}
          </div>
        </form>
      </div>
    </EmblaSlide>
  )
}

function StepSlide({
  step,
  stepNumber,
  totalSteps,
  selectedAnswer,
  selectedOptionTitle,
  selectedFreeTextValue,
  ctaLabel,
  privacyLink,
  isLastStep,
  active,
  isSubmitting,
  canSubmit,
  privacyAccepted,
  onPrivacyChange,
  onBack,
  onFreeTextStepAnswerChange,
  onAnswerSelect,
  onSelectedAnswerFreeTextChange,
  onContinue,
  onSubmit,
}: {
  step: FunnelCompanyProps["content"]["steps"][number]
  stepNumber: number
  totalSteps: number
  selectedAnswer: string
  selectedOptionTitle: string
  selectedFreeTextValue: string
  ctaLabel: string
  privacyLink: ReactNode
  isLastStep: boolean
  active: boolean
  isSubmitting: boolean
  canSubmit: boolean
  privacyAccepted: boolean
  onPrivacyChange: (checked: boolean) => void
  onBack: () => void
  onFreeTextStepAnswerChange: (answer: string) => void
  onAnswerSelect: (answer: FunnelCompanyAnswer) => void
  onSelectedAnswerFreeTextChange: (freeText: string) => void
  onContinue: () => void
  onSubmit: () => Promise<void>
}) {
  const $ = useLocaleContext().$.funnelCompany
  const stepType = step.type || "singleChoice"
  const selectedSingleChoiceAnswer = step.answers.find(
    (answer) => answer.title === selectedOptionTitle
  )
  const selectedSingleChoiceRequiresFreeText =
    !!selectedSingleChoiceAnswer?.allowFreeText

  return (
    <EmblaSlide active={active}>
      <div className="mx-auto flex max-w-5xl flex-col">
        {step.alertTitle && (
          <div className="border-l-2 border-viola-500 bg-viola-50 px-6 py-4">
            <p className="text-md font-bold">{step.alertTitle}</p>
            {step.alertDescription && (
              <p className="mt-1 font-family-gthaptik font-thin text-grey-200">
                {step.alertDescription}
              </p>
            )}
          </div>
        )}

        <div className="mt-6 flex items-center gap-2 text-neutral-dark">
          <CircleArrowLeft
            className="cursor-pointer"
            strokeWidth={1}
            onClick={onBack}
          />
          <p className="font-family-gtpressura text-xs font-light">
            Step {stepNumber} {$.of} {totalSteps}
          </p>
        </div>

        {stepType === "freeText" ? (
          <>
            <Textarea
              className="mt-6"
              label={step.question}
              placeholder={step.answerPlaceholder}
              required
              value={selectedAnswer}
              onChange={(e) => onFreeTextStepAnswerChange(e.target.value)}
            />
            <p className="font-family-gtpressura text-sm font-light text-neutral-dark">
              {selectedAnswer.length} / 200
            </p>

            {!isLastStep ? (
              <Cta
                className="mt-8 flex w-fit items-center gap-2"
                type="button"
                disabled={!active || !selectedAnswer.trim()}
                onClick={onContinue}
              >
                {$.proceed}
              </Cta>
            ) : (
              <>
                <Checkbox
                  className="mt-4"
                  label={privacyLink}
                  checked={privacyAccepted}
                  onChange={onPrivacyChange}
                />
                <Cta
                  className="mt-8 flex w-fit items-center gap-2"
                  type="button"
                  disabled={!canSubmit || !active || isSubmitting}
                  onClick={onSubmit}
                >
                  {isSubmitting && <LoaderCircle className="animate-spin" />}
                  {ctaLabel}
                </Cta>
              </>
            )}
          </>
        ) : (
          <>
            <h2 className="mt-6 font-family-gthaptik text-xl font-light text-primary-800">
              {step.question}
            </h2>
            <div className="mt-6 flex flex-col gap-6">
              {step.answers.map((answer, i) => {
                const isSelected = selectedOptionTitle === answer.title

                return (
                  <div key={i}>
                    <button
                      disabled={!active || isSubmitting}
                      className={`flex w-full items-center justify-between rounded-sm border p-3 text-left font-family-gthaptik font-light text-neutral-dark focus:ring-2 focus:ring-viola-500 focus:outline-none ${
                        isSelected
                          ? "border-viola-500 bg-viola-50"
                          : "border-primary-600 hover:bg-viola-50"
                      }`}
                      onClick={() => {
                        onAnswerSelect(answer)
                        if (!isLastStep && !answer.allowFreeText) {
                          onContinue()
                        }
                      }}
                    >
                      <div>
                        <p className="text-md">{answer.title}</p>
                        {answer.subtitle && (
                          <p className="mt-1 text-sm">{answer.subtitle}</p>
                        )}
                      </div>
                      <ArrowRight />
                    </button>
                    {isSelected && answer.allowFreeText && (
                      <Input
                        className="mt-3"
                        label={answer.title}
                        placeholder={answer.freeTextPlaceholder}
                        required
                        disabled={!active || isSubmitting}
                        value={selectedFreeTextValue}
                        onChange={(e) =>
                          onSelectedAnswerFreeTextChange(e.target.value)
                        }
                      />
                    )}
                  </div>
                )
              })}
            </div>

            {!isLastStep && selectedSingleChoiceRequiresFreeText && (
              <Cta
                className="mt-8 flex items-center gap-2"
                type="button"
                disabled={!active || !selectedAnswer.trim() || isSubmitting}
                onClick={onContinue}
              >
                {$.proceed}
              </Cta>
            )}

            {isLastStep && (
              <>
                <Checkbox
                  className="mt-4"
                  label={privacyLink}
                  checked={privacyAccepted}
                  onChange={onPrivacyChange}
                />
                <Cta
                  className="mt-8 flex w-fit items-center gap-2"
                  type="button"
                  disabled={!canSubmit || !active || isSubmitting}
                  onClick={onSubmit}
                >
                  {isSubmitting && <LoaderCircle className="animate-spin" />}
                  {ctaLabel}
                </Cta>
              </>
            )}
          </>
        )}
      </div>
    </EmblaSlide>
  )
}

function Success({
  title,
  description,
  active,
  socials,
}: {
  title: string
  description: string
  active: boolean
  socials: {
    title?: string
    url: string
    image?: {
      asset: SanityReference<SanityImageAsset>
    }
  }[]
}) {
  const $ = useLocaleContext().$.funnelCompany
  return (
    <EmblaSlide active={active}>
      <div className="mx-auto flex h-full max-w-[69rem] flex-col justify-center">
        <h2 className="text-2xl font-bold text-neutral-dark">{title}</h2>
        <p className="mt-8 font-family-gtpressura text-xlg text-neutral-dark opacity-55">
          {description}
        </p>
        {!!socials.length && (
          <div className="mt-6 flex items-center justify-center gap-4">
            {socials.map((social, i) =>
              social.image?.asset ? (
                <SocialIconLink
                  key={i}
                  url={social.url}
                  asset={social.image.asset}
                  color="blue"
                  title={social.title || "social icon"}
                />
              ) : null
            )}
          </div>
        )}
        <div className="mt-8 flex justify-center">
          <Cta
            sanity={{
              internalLink: {
                slug: "/",
              },
              type: "internal",
            }}
          >
            {$.goHome}
          </Cta>
        </div>
      </div>
    </EmblaSlide>
  )
}

function EmblaSlide({
  active,
  children,
}: {
  active: boolean
  children: ReactNode
}) {
  const slideRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const slide = slideRef.current
    if (!slide) return

    if (active) {
      slide.removeAttribute("inert")
      return
    }

    const focusedElement = document.activeElement
    if (
      focusedElement instanceof HTMLElement &&
      slide.contains(focusedElement)
    ) {
      focusedElement.blur()
    }
    slide.setAttribute("inert", "")
  }, [active])

  return (
    <div
      ref={slideRef}
      aria-hidden={!active}
      className="flex-[0_0_100%] px-4 [&_h1]:text-left [&_h2]:text-left [&_h3]:text-left [&_h4]:text-left [&_label]:text-left [&_p]:text-left"
      data-active={active}
    >
      {children}
    </div>
  )
}